import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";
import { slugify } from "@/lib/slugify";

export interface EmployeeDeductionTypeRecord {
  id: ID;
  code: string;
  label: string;
  sortOrder: number;
  createdAt: string;
}

const LS_KEY = "inventory.employeeDeductionTypes.v1";
const BOOT_KEY = "inventory.employeeDeductionTypes.seeded.v1";

type EmployeeDeductionTypeApiRecord = {
  id?: string | number;
  code?: string | null;
  label?: string | null;
  sort_order?: number | null;
  sortOrder?: number | null;
  created_at?: string | null;
  createdAt?: string | null;
};

type ListApiResponse = { data?: EmployeeDeductionTypeApiRecord[] };
type UpsertApiResponse = { message?: string; data?: EmployeeDeductionTypeApiRecord };
type DeleteApiResponse = { message?: string };

export type UpsertEmployeeDeductionTypeResult = {
  record: EmployeeDeductionTypeRecord;
  message: string;
};

export type DeleteEmployeeDeductionTypeResult = {
  success: boolean;
  message: string;
};

function dedupeById(items: EmployeeDeductionTypeRecord[]): EmployeeDeductionTypeRecord[] {
  const byId = new Map<ID, EmployeeDeductionTypeRecord>();
  for (const item of items) byId.set(item.id, item);
  return Array.from(byId.values());
}

function mapApiRecord(record: EmployeeDeductionTypeApiRecord): EmployeeDeductionTypeRecord {
  const sort = Number(record.sort_order ?? record.sortOrder ?? 0) || 0;
  const created = String(record.created_at ?? record.createdAt ?? new Date().toISOString());
  return {
    id: String(record.id ?? ""),
    code: String(record.code ?? "").trim(),
    label: String(record.label ?? "").trim(),
    sortOrder: sort,
    createdAt: created,
  };
}

function load(): EmployeeDeductionTypeRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as EmployeeDeductionTypeRecord[];
    return Array.isArray(parsed) ? dedupeById(parsed) : [];
  } catch {
    return [];
  }
}

function save(items: EmployeeDeductionTypeRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

function extractRowsFromResponse(body: unknown): EmployeeDeductionTypeApiRecord[] {
  if (Array.isArray(body)) return body as EmployeeDeductionTypeApiRecord[];
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;

  const fromBlock = (block: unknown): EmployeeDeductionTypeApiRecord[] | null => {
    if (Array.isArray(block)) return block as EmployeeDeductionTypeApiRecord[];
    if (block && typeof block === "object" && !Array.isArray(block)) {
      const inner = (block as Record<string, unknown>).data;
      if (Array.isArray(inner)) return inner as EmployeeDeductionTypeApiRecord[];
    }
    return null;
  };

  for (const key of ["data", "items", "employee_deduction_types", "deduction_types", "results"]) {
    const hit = fromBlock(root[key]);
    if (hit) return hit;
  }
  return [];
}

function readLastPageFromListBody(body: unknown): number | null {
  if (!body || typeof body !== "object") return null;
  const meta = (body as Record<string, unknown>).meta;
  if (meta && typeof meta === "object" && !Array.isArray(meta)) {
    const lp = (meta as Record<string, unknown>).last_page;
    if (typeof lp === "number" && lp >= 1) return lp;
  }
  return null;
}

function extractDeductionTypeFromUpsertBody(body: unknown): EmployeeDeductionTypeApiRecord | null {
  if (!body || typeof body !== "object") return null;
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const block = data as Record<string, unknown>;
    if ("id" in block || typeof block.label === "string" || typeof block.code === "string") {
      return block as unknown as EmployeeDeductionTypeApiRecord;
    }
    const nested = block.employee_deduction_type ?? block.deduction_type;
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      return nested as unknown as EmployeeDeductionTypeApiRecord;
    }
  }
  const top = root.employee_deduction_type ?? root.deduction_type;
  if (top && typeof top === "object" && !Array.isArray(top)) {
    return top as unknown as EmployeeDeductionTypeApiRecord;
  }
  if ("id" in root && root.id != null) {
    return root as unknown as EmployeeDeductionTypeApiRecord;
  }
  return null;
}

export function listEmployeeDeductionTypeRecords(): EmployeeDeductionTypeRecord[] {
  return load().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.label.localeCompare(b.label));
}

export function getEmployeeDeductionTypeById(id: ID): EmployeeDeductionTypeRecord | undefined {
  return load().find((t) => t.id === id);
}

/** Match API / legacy deduction name to a catalog row (case-insensitive label). */
export function findEmployeeDeductionTypeIdByLabel(needle: string): string {
  const t = needle.trim().toLowerCase();
  if (!t) return "";
  const hit = load().find((x) => x.label.toLowerCase() === t);
  return hit?.id ?? "";
}

export function codeExists(code: string, exceptId?: ID): boolean {
  const c = slugify(code);
  return load().some((t) => slugify(t.code) === c && t.id !== exceptId);
}

const EMPLOYEE_DEDUCTION_TYPES_PER_PAGE = 20;

export async function syncEmployeeDeductionTypesFromApi(): Promise<EmployeeDeductionTypeRecord[]> {
  try {
    const collected: EmployeeDeductionTypeApiRecord[] = [];
    let page = 1;
    let lastPage = 1;
    do {
      const response = await api.get<ListApiResponse>("/api/v1/employee-deduction-types", {
        params: { page, per_page: EMPLOYEE_DEDUCTION_TYPES_PER_PAGE },
      });
      const body = response.data;
      collected.push(...extractRowsFromResponse(body));
      lastPage = readLastPageFromListBody(body) ?? 1;
      page += 1;
    } while (page <= lastPage && page <= 50);

    const records = dedupeById(collected.map(mapApiRecord).filter((r) => Boolean(r.id)));
    save(records);
  } catch (error) {
    console.error("[EmployeeDeductionTypes] sync failed", error);
    throw error;
  }
  return listEmployeeDeductionTypeRecords();
}

export async function createEmployeeDeductionType(data: {
  code: string;
  label: string;
  sortOrder?: number;
}): Promise<UpsertEmployeeDeductionTypeResult> {
  const sortOrder = data.sortOrder ?? 0;
  const payload = {
    code: slugify(data.code),
    label: data.label.trim(),
    sort_order: sortOrder,
  };
  const response = await api.post<UpsertApiResponse>("/api/v1/employee-deduction-types", payload);
  const created = extractDeductionTypeFromUpsertBody(response.data);
  if (!created || created.id == null) {
    throw new Error("Invalid create deduction type response.");
  }
  const record = mapApiRecord(created);
  const items = load();
  const idx = items.findIndex((t) => t.id === record.id);
  if (idx >= 0) items[idx] = record;
  else items.push(record);
  save(items);
  return { record, message: response.data?.message || "Deduction type created." };
}

export async function updateEmployeeDeductionType(
  id: ID,
  data: { code: string; label: string; sortOrder?: number },
): Promise<UpsertEmployeeDeductionTypeResult | null> {
  const items = load();
  const i = items.findIndex((t) => t.id === id);
  if (i === -1) return null;
  const payload = {
    code: slugify(data.code) || items[i].code,
    label: data.label.trim(),
    sort_order: data.sortOrder ?? items[i].sortOrder,
  };
  const response = await api.put<UpsertApiResponse>(`/api/v1/employee-deduction-types/${id}`, payload);
  let updated = extractDeductionTypeFromUpsertBody(response.data);
  if (!updated) throw new Error("Invalid update deduction type response.");
  if (updated.id == null || String(updated.id) === "") {
    updated = { ...updated, id };
  }
  items[i] = mapApiRecord(updated);
  save(items);
  return { record: items[i], message: response.data?.message || "Deduction type updated." };
}

export async function deleteEmployeeDeductionType(id: ID): Promise<DeleteEmployeeDeductionTypeResult> {
  const response = await api.delete<DeleteApiResponse>(`/api/v1/employee-deduction-types/${id}`);
  const items = load();
  const next = items.filter((t) => t.id !== id);
  const removed = next.length !== items.length;
  if (removed) save(next);
  return {
    success: removed,
    message:
      response.data?.message || (removed ? "Deduction type removed." : "Deduction type was not found."),
  };
}

/** Seed common deduction names when storage is empty. */
export function seedEmployeeDeductionTypesIfEmpty() {
  if (typeof localStorage === "undefined") return;
  if (localStorage.getItem(BOOT_KEY)) return;
  if (load().length > 0) {
    localStorage.setItem(BOOT_KEY, "1");
    return;
  }
  const now = new Date().toISOString();
  const seed: EmployeeDeductionTypeRecord[] = [
    { id: "edt_pf", code: "pf", label: "Provident Fund (PF)", sortOrder: 1, createdAt: now },
    { id: "edt_tax", code: "tax", label: "Tax", sortOrder: 2, createdAt: now },
    { id: "edt_eobi", code: "eobi", label: "EOBI", sortOrder: 3, createdAt: now },
    { id: "edt_other", code: "other", label: "Other", sortOrder: 99, createdAt: now },
  ];
  save(seed);
  localStorage.setItem(BOOT_KEY, "1");
}

